// src/component/MusicPlaybackIssueMonitor.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  Tooltip,
  IconButton,
  LinearProgress,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";

import { db } from "../api/firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { AppThemeContext } from "./AppShell";

const COLLECTION_NAME = "musicPlaybackIssues2025";

export default function MusicPlaybackIssueMonitor() {
  const theme = React.useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";
  const surface = theme.surfaceBg ?? "#FFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [lastUpdated, setLastUpdated] = useState(null);

  // ─────────────────────────────
  // Firestore 購読
  // ─────────────────────────────
  const setupSubscription = () => {
    setLoading(true);
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = [];
        snap.forEach((docSnap) => {
          items.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
        setLogs(items);
        setLoading(false);
        setLastUpdated(new Date());
      },
      (err) => {
        console.error("Failed to subscribe playback issues:", err);
        setLoading(false);
      }
    );

    return unsub;
  };

  useEffect(() => {
    const unsub = setupSubscription();
    return () => {
      if (unsub) unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────
  // フィルタリング
  // ─────────────────────────────
  const filteredLogs = useMemo(() => {
    if (selectedEvent === "all") return logs;
    return logs.filter((log) => log.event === selectedEvent);
  }, [logs, selectedEvent]);

  // ─────────────────────────────
  // 表示用フォーマッタ
  // ─────────────────────────────
  const formatDateTime = (ts) => {
    if (!ts) return "-";
    try {
      const d = typeof ts.toDate === "function" ? ts.toDate() : ts;
      return d.toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const eventMeta = (event) => {
    switch (event) {
      case "stalled":
        return {
          label: "stalled",
          color: "warning",
          icon: <WifiOffRoundedIcon fontSize="small" />,
          description: "データ供給が止まった（ネットワーク詰まり）",
        };
      case "waiting":
        return {
          label: "waiting",
          color: "default",
          icon: <PauseCircleOutlineRoundedIcon fontSize="small" />,
          description: "再生に必要なデータ待ち",
        };
      case "error":
        return {
          label: "error",
          color: "error",
          icon: <ErrorOutlineRoundedIcon fontSize="small" />,
          description: "再生エラー（ソース不正 / 404 など）",
        };
      case "freeze":
        return {
          label: "freeze",
          color: "secondary",
          icon: <WarningAmberRoundedIcon fontSize="small" />,
          description: "時間は経っているのに currentTime が進んでいない",
        };
      default:
        return {
          label: event || "unknown",
          color: "default",
          icon: <WarningAmberRoundedIcon fontSize="small" />,
          description: "",
        };
    }
  };

  // UA を人間向けに整形
  const parseUserAgentForDisplay = (ua) => {
    if (!ua || typeof ua !== "string") return null;

    const isIphone = ua.includes("iPhone");
    const isIpad = ua.includes("iPad");
    const isAndroid = /Android/i.test(ua);

    let device = "不明デバイス";
    if (isIphone) device = "iPhone";
    else if (isIpad) device = "iPad";
    else if (isAndroid) device = "Android端末";

    // OS バージョン
    let os = "不明OS";
    const iosMatch = ua.match(/iPhone OS ([0-9_]+)/);
    if (iosMatch) {
      os = `iOS ${iosMatch[1].replace(/_/g, ".")}`;
    }
    const androidMatch = ua.match(/Android ([0-9.]+)/);
    if (androidMatch) {
      os = `Android ${androidMatch[1]}`;
    }

    // Safari / Chrome バージョン
    let browser = "不明ブラウザ";
    const safariVersion = ua.match(/Version\/([\d._]+)/);
    if (safariVersion && ua.includes("Safari")) {
      browser = `Safari ${safariVersion[1]}`;
    }
    const chromeMatch = ua.match(/Chrome\/([\d.]+)/);
    if (chromeMatch) {
      browser = `Chrome ${chromeMatch[1]}`;
    }

    // WebKit / Blink など
    let engine = "不明エンジン";
    const webkitMatch = ua.match(/AppleWebKit\/([\d.]+)/);
    if (webkitMatch) {
      engine = `WebKit ${webkitMatch[1]}`;
    }

    return { device, os, browser, engine, raw: ua };
  };

  // ─────────────────────────────
  // UI
  // ─────────────────────────────
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          MONITORING
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.03em",
            color: textBase,
          }}
        >
          音楽再生ログ監視
        </Typography>

        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.7)" }}>
          Topページなどで発生した音源の再生エラー／フリーズなどを確認できます。
        </Typography>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mt: 1.5, flexWrap: "wrap" }}
        >
          {/* フィルタ */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {[
              { value: "all", label: "すべて" },
              { value: "stalled", label: "stalled" },
              { value: "waiting", label: "waiting" },
              { value: "error", label: "error" },
              { value: "freeze", label: "freeze" },
            ].map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                clickable
                color={selectedEvent === opt.value ? "primary" : "default"}
                variant={selectedEvent === opt.value ? "filled" : "outlined"}
                onClick={() => setSelectedEvent(opt.value)}
              />
            ))}
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            {lastUpdated && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.6)" }}
              >
                最終更新: {formatDateTime(lastUpdated)}
              </Typography>
            )}
            <Tooltip title="購読を再接続します">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    // 今は見た目上の更新のみ
                    setLastUpdated(new Date());
                  }}
                >
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* ログ一覧 */}
      {filteredLogs.length === 0 && !loading ? (
        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            border: `1px dashed ${border}`,
            bgcolor: surface,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "rgba(0,0,0,0.6)", mb: 0.5 }}
          >
            表示できるログがありません。
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
            まだエラーやフリーズが発生していないか、
            ログ収集コードが未反映の可能性があります。
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {filteredLogs.map((log) => {
            const meta = eventMeta(log.event);
            const state = log.state || {};
            const current = state.currentTime ?? null;
            const duration = state.duration ?? null;
            const pos =
              current != null && duration
                ? `${current.toFixed(1)} / ${duration.toFixed(1)}`
                : "-";

            const uaInfo = parseUserAgentForDisplay(log.userAgent);

            return (
              <Card
                key={log.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
                }}
              >
                <CardContent sx={{ p: 2.2 }}>
                  <Stack spacing={1.2}>
                    {/* 上段：イベント種別・日時 */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          icon={meta.icon}
                          label={meta.label}
                          size="small"
                          color={meta.color}
                          variant="filled"
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(0,0,0,0.6)" }}
                        >
                          {meta.description}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(0,0,0,0.55)" }}
                      >
                        {formatDateTime(log.createdAt)}
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 0.5 }} />

                    {/* 中段：track / user / 再生位置 */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      sx={{ fontSize: 13 }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(0,0,0,0.5)" }}
                        >
                          trackId
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: textBase,
                            wordBreak: "break-all",
                          }}
                        >
                          {log.trackId || "-"}
                        </Typography>
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(0,0,0,0.5)" }}
                        >
                          userId
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: textBase,
                            wordBreak: "break-all",
                          }}
                        >
                          {log.userId || "-"}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 160 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(0,0,0,0.5)" }}
                        >
                          再生位置
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: textBase }}
                        >
                          {pos} sec
                        </Typography>
                      </Box>
                    </Stack>

                    {/* 下段：readyState / networkState / UA */}
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(0,0,0,0.55)" }}
                      >
                        readyState: {state.readyState ?? "-"} / networkState:{" "}
                        {state.networkState ?? "-"} / paused:{" "}
                        {String(state.paused ?? "").toString()} / ended:{" "}
                        {String(state.ended ?? "").toString()}
                      </Typography>

                      {log.extra && (
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(0,0,0,0.55)" }}
                        >
                          extra: {JSON.stringify(log.extra)}
                        </Typography>
                      )}

                      {uaInfo && (
                        <Stack spacing={0.2}>
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(0,0,0,0.6)" }}
                          >
                            📱 デバイス: {uaInfo.device}（{uaInfo.os}）
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(0,0,0,0.6)" }}
                          >
                            🧭 ブラウザ: {uaInfo.browser}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(0,0,0,0.6)" }}
                          >
                            🖥 エンジン: {uaInfo.engine}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(0,0,0,0.45)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={uaInfo.raw}
                          >
                            UA: {uaInfo.raw}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
